/**
 * @workspace/crypto — WebCrypto primitives for chain-scoped E2EE.
 *
 * All operations use the platform WebCrypto API (available natively in
 * browsers and via expo-crypto's `subtle` shim on React Native). No
 * Node-only APIs are used.
 *
 * Cipher suite:
 *   Data encryption : AES-256-GCM   (nonce prepended to ciphertext)
 *   Key wrapping    : RSA-OAEP-2048 with SHA-256
 *   Passphrase KDF  : PBKDF2 (200 000 × SHA-256) → AES-KW-256
 */

// ── Encrypted-object metadata tags ──────────────────────────────────────
//
// Every E2EE file upload (consultation PDFs, photos) writes this custom
// metadata key/value pair on the Storage object so downloaders can tell an
// encrypted object apart from a legacy pre-E2EE plaintext one. Single
// source of truth for both the upload path (artifacts/ccs-pharmacy's
// cloudUpload.ts) and the download/decrypt path (the admin panels'
// getConsultationPdfUrl callable) — do not re-declare these literals.

export const ENCRYPTED_FILE_METADATA_KEY = "ccs-encrypted";
export const ENCRYPTED_FILE_METADATA_VALUE = "v1-aes-gcm";

// ── Internal helpers ─────────────────────────────────────────────────────

/**
 * Ensure a Uint8Array is backed by a plain ArrayBuffer (not SharedArrayBuffer
 * or a view into a larger buffer). TypeScript 5.3+ added generics to Uint8Array
 * and WebCrypto APIs now require ArrayBufferView<ArrayBuffer>. Any Uint8Array
 * that is a view into another buffer (e.g. from .slice()) may carry an
 * ArrayBufferLike typed .buffer property; this helper copies the bytes into
 * a fresh ArrayBuffer so the cast is safe.
 */
function asCleanBuffer(u8: Uint8Array): ArrayBuffer {
  // Fast path: it's already a standalone ArrayBuffer view with no offset.
  if (
    u8.buffer instanceof ArrayBuffer &&
    u8.byteOffset === 0 &&
    u8.byteLength === u8.buffer.byteLength
  ) {
    return u8.buffer;
  }
  const out = new ArrayBuffer(u8.byteLength);
  new Uint8Array(out).set(u8);
  return out;
}

/** Encode Uint8Array → URL-safe base64 string. */
export function bufToBase64(buf: Uint8Array): string {
  if (typeof btoa === "function") {
    let s = "";
    for (let i = 0; i < buf.length; i++) s += String.fromCharCode(buf[i]!);
    return btoa(s);
  }
  // Node / older RN environments
  return (globalThis as { Buffer?: { from(b: Uint8Array): { toString(e: string): string } } })
    .Buffer!.from(buf)
    .toString("base64");
}

/** Decode base64 string → Uint8Array. */
export function base64ToBuf(b64: string): Uint8Array {
  if (typeof atob === "function") {
    const s = atob(b64);
    const out = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
    return out;
  }
  return Uint8Array.from(
    (globalThis as { Buffer?: { from(s: string, e: string): ArrayLike<number> } })
      .Buffer!.from(b64, "base64"),
  );
}

// ── AES-256-GCM symmetric encryption ────────────────────────────────────

/** Generate a fresh random AES-256-GCM chain key. */
export async function generateChainKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
}

/**
 * Encrypt a UTF-8 string with AES-256-GCM.
 * Returns base64(12-byte-nonce || ciphertext).
 */
export async function encrypt(plaintext: string, chainKey: CryptoKey): Promise<string> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce },
    chainKey,
    new TextEncoder().encode(plaintext),
  );
  const ctBytes = new Uint8Array(ct);
  const combined = new Uint8Array(12 + ctBytes.length);
  combined.set(nonce, 0);
  combined.set(ctBytes, 12);
  return bufToBase64(combined);
}

/**
 * Decrypt an AES-256-GCM payload back to a UTF-8 string.
 *
 * Accepts BOTH wire framings:
 *   canonical : base64(12-byte-nonce || ciphertext)      ← what encrypt() emits
 *   legacy    : base64(nonce) + "." + base64(ciphertext) ← historical booking
 *               web-app framing; still present in stored booking records that
 *               can never be re-encrypted, so it must stay readable forever.
 *
 * "." is not a base64 alphabet character, so the two framings are
 * unambiguous.
 *
 * Throws on authentication failure (tampered data).
 */
export async function decrypt(ciphertext: string, chainKey: CryptoKey): Promise<string> {
  let nonce: Uint8Array;
  let ct: Uint8Array;

  if (ciphertext.includes(".")) {
    const parts = ciphertext.split(".");
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
      throw new Error("E2EE: malformed dot-separated ciphertext");
    }
    nonce = base64ToBuf(parts[0]!);
    ct = base64ToBuf(parts[1]!);
    if (nonce.length !== 12) throw new Error("E2EE: bad nonce length");
    if (ct.length < 1) throw new Error("E2EE: ciphertext too short");
  } else {
    const combined = base64ToBuf(ciphertext);
    if (combined.length < 13) throw new Error("E2EE: ciphertext too short");
    nonce = combined.slice(0, 12);
    ct = combined.slice(12);
  }

  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: asCleanBuffer(nonce) },
    chainKey,
    asCleanBuffer(ct),
  );
  return new TextDecoder().decode(pt);
}

/**
 * Encrypt raw bytes with AES-256-GCM.
 * Returns Uint8Array(nonce || ciphertext) without base64 encoding.
 */
export async function encryptBytes(data: Uint8Array, chainKey: CryptoKey): Promise<Uint8Array> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: asCleanBuffer(nonce) },
    chainKey,
    asCleanBuffer(data),
  );
  const ctBytes = new Uint8Array(ct);
  const combined = new Uint8Array(12 + ctBytes.length);
  combined.set(nonce, 0);
  combined.set(ctBytes, 12);
  return combined;
}

/**
 * Decrypt Uint8Array(nonce || ciphertext) back to raw bytes.
 */
export async function decryptBytes(data: Uint8Array, chainKey: CryptoKey): Promise<Uint8Array> {
  if (data.length < 13) throw new Error("E2EE: encrypted data too short");
  const nonce = data.slice(0, 12);
  const ct = data.slice(12);
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: asCleanBuffer(nonce) },
    chainKey,
    asCleanBuffer(ct),
  );
  return new Uint8Array(pt);
}

// ── RSA-OAEP-2048 user keypair ───────────────────────────────────────────

/**
 * Generate a fresh RSA-OAEP-2048 keypair.
 *
 * @param extractable Set true on mobile so the private key can be exported
 *   to JWK for SecureStore persistence. Set false on web where the key can
 *   be stored as a CryptoKey object in IndexedDB.
 */
export async function generateUserKeypair(extractable = false): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: "SHA-256",
    },
    extractable,
    ["wrapKey", "unwrapKey"],
  );
}

/** Export a public key to SPKI base64. */
export async function exportPublicKey(publicKey: CryptoKey): Promise<string> {
  const spki = await crypto.subtle.exportKey("spki", publicKey);
  return bufToBase64(new Uint8Array(spki));
}

/** Import a public key from SPKI base64. */
export async function importPublicKey(spkiBase64: string): Promise<CryptoKey> {
  const spki = base64ToBuf(spkiBase64);
  return crypto.subtle.importKey(
    "spki",
    asCleanBuffer(spki),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["wrapKey"],
  );
}

/**
 * Re-derive the SPKI-base64 public key that corresponds to an RSA-OAEP
 * private key, using the public modulus/exponent embedded in its JWK
 * representation. Requires the private key to be extractable (true on
 * mobile, where SecureStore persistence round-trips through JWK export via
 * {@link exportPrivateKeyAsJwk} / {@link importPrivateKeyFromJwk}).
 *
 * Lets a device that already holds a local private key republish the exact
 * matching public key to the server without generating a brand new keypair
 * (which would strand every existing wrappedKey slot for the user, since
 * those were wrapped under the OLD public key).
 */
export async function derivePublicKeyFromPrivateKey(privateKey: CryptoKey): Promise<string> {
  const jwk = await crypto.subtle.exportKey("jwk", privateKey);
  const publicJwk: JsonWebKey = {
    kty: jwk.kty,
    n: jwk.n,
    e: jwk.e,
    alg: jwk.alg,
    ext: true,
    key_ops: ["wrapKey"],
  };
  const publicKey = await crypto.subtle.importKey(
    "jwk",
    publicJwk,
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["wrapKey"],
  );
  return exportPublicKey(publicKey);
}

/** Export a private key to JWK JSON string (for mobile SecureStore persistence). */
export async function exportPrivateKeyAsJwk(privateKey: CryptoKey): Promise<string> {
  const jwk = await crypto.subtle.exportKey("jwk", privateKey);
  return JSON.stringify(jwk);
}

/** Import a private key from JWK JSON string (mobile SecureStore restore). */
export async function importPrivateKeyFromJwk(jwkJson: string): Promise<CryptoKey> {
  const jwk = JSON.parse(jwkJson) as JsonWebKey;
  return crypto.subtle.importKey("jwk", jwk, { name: "RSA-OAEP", hash: "SHA-256" }, true, [
    "unwrapKey",
  ]);
}

// ── RSA-OAEP key wrapping ────────────────────────────────────────────────

/**
 * Wrap a chain key under a recipient's RSA-OAEP public key.
 * Returns base64(wrapped-key-bytes).
 */
export async function wrapKeyForUser(
  chainKey: CryptoKey,
  recipientPublicKey: CryptoKey,
): Promise<string> {
  const wrapped = await crypto.subtle.wrapKey("raw", chainKey, recipientPublicKey, {
    name: "RSA-OAEP",
  });
  return bufToBase64(new Uint8Array(wrapped));
}

/**
 * Unwrap a chain key from a base64 blob using the caller's RSA-OAEP private key.
 */
export async function unwrapKeyForUser(
  wrappedKeyBase64: string,
  privateKey: CryptoKey,
): Promise<CryptoKey> {
  const wrapped = base64ToBuf(wrappedKeyBase64);
  return crypto.subtle.unwrapKey(
    "raw",
    asCleanBuffer(wrapped),
    privateKey,
    { name: "RSA-OAEP" },
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
}

// ── PBKDF2 + AES-KW passphrase key wrapping ──────────────────────────────

/**
 * Wrap a chain key using a passphrase (PBKDF2 → AES-KW).
 * Returns base64(16-byte-salt || wrapped-key).
 */
export async function wrapKeyWithPassphrase(
  chainKey: CryptoKey,
  passphrase: string,
): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const kek = await deriveKek(passphrase, salt);
  const wrapped = await crypto.subtle.wrapKey("raw", chainKey, kek, "AES-KW");
  const wrappedBytes = new Uint8Array(wrapped);
  const combined = new Uint8Array(16 + wrappedBytes.length);
  combined.set(salt, 0);
  combined.set(wrappedBytes, 16);
  return bufToBase64(combined);
}

/**
 * Unwrap a chain key from a base64(salt || wrapped-key) blob using a passphrase.
 */
export async function unwrapKeyWithPassphrase(
  blob: string,
  passphrase: string,
): Promise<CryptoKey> {
  const combined = base64ToBuf(blob);
  if (combined.length < 17) throw new Error("E2EE: passphrase-wrapped key blob too short");
  const salt = combined.slice(0, 16);
  const wrappedKey = combined.slice(16);
  const kek = await deriveKek(passphrase, salt);
  return crypto.subtle.unwrapKey(
    "raw",
    asCleanBuffer(wrappedKey),
    kek,
    "AES-KW",
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
}

// ── Booking hybrid encryption (RSA-OAEP + AES-GCM) ─────────────────────────
//
// Per-chain RSA booking keypairs let the unauthenticated patient booking web
// app encrypt patient data before sending it to the server. Only pharmacists
// (who can decrypt the chain's booking private key using the chain AES key)
// can read the plaintext.
//
// Wire-format: "hybrid1:" prefix + base64(JSON({ wrappedKey, ciphertext }))
//   wrappedKey  = RSA-OAEP(ephemeralAesKey, bookingPublicKey)
//   ciphertext  = AES-GCM(plaintext, ephemeralAesKey)
//                 canonical framing: base64(iv || ct)  (what encrypt() emits)
//                 legacy framing:    base64(iv) + "." + base64(ct)
//                 decrypt() reads both; producers must emit the canonical one.
//
// The prefix "hybrid1:" distinguishes this from legacy AES-only ciphertext.
//
// ⚠️ DRIFT WARNING — this wire format has THREE producers. This file is the
// canonical one and holds the only consumer; the other two exist because
// neither surface can depend on @workspace/crypto:
//   artifacts/booking/src/lib/api.ts     the booking web app
//   artifacts/functions/src/lib/bookingPatientCrypto.ts
//                                        the WhatsApp path, which has no
//                                        client to seal in and so seals
//                                        server-side (Cloud Functions cannot
//                                        resolve a workspace TS package)
// They are ONE logical wire format: any change here must be mirrored in both
// in the same change, and stored records in the legacy framing must stay
// decryptable. The agreement is asserted by
// artifacts/functions/test/crossSurfaceContracts.test.ts.

/**
 * Generate an extractable RSA-OAEP keypair for use as a per-chain booking
 * encryption keypair.  Extractable so the private key can be exported to JWK
 * and wrapped with the chain AES key for storage.
 */
export async function generateBookingKeypair(): Promise<CryptoKeyPair> {
  return generateUserKeypair(true);
}

/**
 * Hybrid-encrypt `plaintext` for a booking public key.
 *
 * Generates an ephemeral AES-256-GCM key, encrypts the plaintext with it,
 * then wraps the ephemeral key with the booking RSA public key so that only
 * the holder of the corresponding private key can decrypt.
 *
 * Returns a portable string with a "hybrid1:" prefix.
 */
export async function hybridEncryptForPublicKey(
  publicKey: CryptoKey,
  plaintext: string,
): Promise<string> {
  const ephemeralKey = await generateChainKey();
  const ciphertext = await encrypt(plaintext, ephemeralKey);
  const wrappedKey = await wrapKeyForUser(ephemeralKey, publicKey);
  return "hybrid1:" + btoa(JSON.stringify({ wrappedKey, ciphertext }));
}

/**
 * Hybrid-decrypt a value produced by {@link hybridEncryptForPublicKey}.
 *
 * Unwraps the ephemeral AES key with the booking RSA private key, then
 * decrypts the ciphertext payload.
 */
export async function hybridDecryptWithPrivateKey(
  privateKey: CryptoKey,
  blob: string,
): Promise<string> {
  const suffix = blob.startsWith("hybrid1:") ? blob.slice("hybrid1:".length) : blob;
  const { wrappedKey, ciphertext } = JSON.parse(atob(suffix)) as {
    wrappedKey: string;
    ciphertext: string;
  };
  const ephemeralKey = await unwrapKeyForUser(wrappedKey, privateKey);
  return decrypt(ciphertext, ephemeralKey);
}

async function deriveKek(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: asCleanBuffer(salt), iterations: 200_000, hash: "SHA-256" },
    base,
    { name: "AES-KW", length: 256 },
    false,
    ["wrapKey", "unwrapKey"],
  );
}
